import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { MapPin, Route, Clock, Car } from 'lucide-react';
import { cn } from '@/lib/utils';

interface RouteSummaryCardProps {
  stopCount: number;
  totalDistance: number; // in miles
  totalDuration: number; // in minutes
  technicianName?: string;
  className?: string;
}

/**
 * Summary card showing route statistics
 * Displays stop count, estimated distance, and duration
 */
export const RouteSummaryCard: React.FC<RouteSummaryCardProps> = ({
  stopCount,
  totalDistance,
  totalDuration,
  technicianName,
  className = ''
}) => {
  // Format duration to hours and minutes
  const formatDuration = (minutes: number): string => {
    if (minutes < 60) {
      return `${Math.round(minutes)} min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (mins === 0) {
      return `${hours}h`;
    }
    return `${hours}h ${mins}m`;
  };

  // Format distance
  const formatDistance = (miles: number): string => {
    if (miles < 1) {
      return `${Math.round(miles * 5280)} ft`;
    }
    return `${miles.toFixed(1)} mi`;
  };

  return (
    <Card className={cn('bg-card border', className)}>
      <CardContent className='p-4'>
        {/* Header */}
        {technicianName && (
          <div className='flex items-center gap-2 mb-3 pb-3 border-b'>
            <Car className='h-4 w-4 text-primary' />
            <span className='font-medium text-sm text-foreground'>
              {technicianName}&apos;s Route
            </span>
          </div>
        )}

        {/* Stats Grid */}
        <div className='grid grid-cols-3 gap-4'>
          {/* Stops */}
          <div className='flex flex-col items-center text-center'>
            <div className='flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 mb-2'>
              <MapPin className='h-5 w-5 text-primary' />
            </div>
            <span className='text-2xl font-bold text-foreground'>{stopCount}</span>
            <span className='text-xs text-muted-foreground'>Stop{stopCount !== 1 ? 's' : ''}</span>
          </div>

          {/* Distance */}
          <div className='flex flex-col items-center text-center'>
            <div className='flex items-center justify-center w-10 h-10 rounded-full bg-accent/10 mb-2'>
              <Route className='h-5 w-5 text-accent' />
            </div>
            <span className='text-2xl font-bold text-foreground'>
              {totalDistance > 0 ? formatDistance(totalDistance) : '—'}
            </span>
            <span className='text-xs text-muted-foreground'>Distance</span>
          </div>

          {/* Duration */}
          <div className='flex flex-col items-center text-center'>
            <div className='flex items-center justify-center w-10 h-10 rounded-full bg-green-500/10 mb-2'>
              <Clock className='h-5 w-5 text-green-600 dark:text-green-400' />
            </div>
            <span className='text-2xl font-bold text-foreground'>
              {totalDuration > 0 ? formatDuration(totalDuration) : '—'}
            </span>
            <span className='text-xs text-muted-foreground'>Est. Time</span>
          </div>
        </div>

        {/* Empty State */}
        {stopCount === 0 && (
          <div className='mt-4 pt-4 border-t text-center'>
            <p className='text-sm text-muted-foreground'>Assign recalls to see route preview</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RouteSummaryCard;
